import { createSelector } from 'reselect'
import { findPostPurchaseCheckout } from 'lib/checkout/checkoutUtils'
import { getCurrentIntl } from 'i18n/util/currentIntl'

function formatPassengerSummary(numAdults: number, numChildren: number, numInfants: number): string {
  const intl = getCurrentIntl()
  const parts: Array<string> = []

  if (numAdults > 0) {
    parts.push(intl.formatMessage({
      id: 'DYZvrvG87g1VMJecUwRw',
      defaultMessage: '{count, plural, one {# Adult} other {# Adults}}',
      description: 'Charter upgrade passenger count label for adults in the checkout price breakdown',
    }, { count: numAdults }))
  }

  if (numChildren > 0) {
    parts.push(intl.formatMessage({
      id: 'BhMg1iglI3uJr64sGYXO',
      defaultMessage: '{count, plural, one {# Child} other {# Children}}',
      description: 'Charter upgrade passenger count label for children in the checkout price breakdown',
    }, { count: numChildren }))
  }

  if (numInfants > 0) {
    parts.push(intl.formatMessage({
      id: 'mPseBxFF1YkzjDMC8BMz',
      defaultMessage: '{count, plural, one {# Infant} other {# Infants}}',
      description: 'Charter upgrade passenger count label for infants in the checkout price breakdown',
    }, { count: numInfants }))
  }

  return parts.join(', ')
}

function getPassengerCounts(travellers: Array<App.FlightTraveller>) {
  let numAdults = 0
  let numChildren = 0
  let numInfants = 0

  for (const t of travellers) {
    const type = t.type?.toLowerCase?.() ?? 'adult'
    if (type === 'child' || type === 'chd' || type === 'cnn') {
      numChildren++
    } else if (type === 'infant' || type === 'inf' || type === 'ins') {
      numInfants++
    } else {
      numAdults++
    }
  }

  if (numAdults === 0 && numChildren === 0 && numInfants === 0) {
    numAdults = 1
  }

  return { numAdults, numChildren, numInfants }
}

interface CharterUpgradeState {
  legs: Array<{ cabin: string; direction: string; adultPrice?: number }>
  searchId?: string
  flightItemId: string
}

function calculateUpgradePrice(
  upgradeOption: { adultPrice: number; childPrice: number; infantPrice: number },
  passengerCounts: { numAdults: number; numChildren: number; numInfants: number },
): number {
  return upgradeOption.adultPrice * passengerCounts.numAdults +
    upgradeOption.childPrice * passengerCounts.numChildren +
    upgradeOption.infantPrice * passengerCounts.numInfants
}

function getSeatDeduction(
  reservationAddons: App.FlightOrderItemDetails['reservationAddons'],
  upgradeFlightIds: Set<string>,
): number {
  return (reservationAddons ?? [])
    .filter(
      (a) =>
        a.addon_type === 'SEATS' &&
        a.status === 'confirmed' &&
        (a.flightId ? upgradeFlightIds.has(a.flightId) : true),
    )
    .reduce((sum, a) => sum + a.price, 0)
}

interface AvailableUpgrade {
  toCabinClass: string
  direction: string
  adultPrice: number
  childPrice: number
  infantPrice: number
  flightIds: Array<string>
}

export function findMatchingUpgrades(
  availableUpgrades: Array<AvailableUpgrade>,
  cabin: string,
  direction: string,
) {
  if (direction === 'both') {
    // When user selected both legs, find outbound + return options separately
    const outbound = availableUpgrades.find(u => u.toCabinClass === cabin && (u.direction === 'outbound' || u.direction === 'both'))
    const returnLeg = availableUpgrades.find(u => u.toCabinClass === cabin && (u.direction === 'return' || u.direction === 'both'))
    // If an option covers 'both' directions, don't double-count it
    if (outbound && returnLeg && outbound === returnLeg) {
      return [outbound]
    }
    return [outbound, returnLeg].filter(Boolean) as typeof availableUpgrades
  }
  const match = availableUpgrades.find(
    u => u.toCabinClass === cabin && (u.direction === direction || u.direction === 'both'),
  )
  return match ? [match] : []
}

export function getCharterUpgradeTotalPrice(
  charterUpgrade: CharterUpgradeState,
  flightDetails: App.State['orders']['flightDetails'],
): number {
  const details = flightDetails[charterUpgrade.flightItemId]
  if (!details?.flight) return 0

  const flight = details.flight
  const availableUpgrades = flight.upgradeOptions?.availableUpgrades ?? []
  const passengerCounts = getPassengerCounts(flight.travellers ?? [])

  let upgradesTotal = 0
  const allUpgradeFlightIds = new Set<string>()

  for (const leg of charterUpgrade.legs) {
    const upgrades = findMatchingUpgrades(availableUpgrades, leg.cabin, leg.direction)
    for (const u of upgrades) {
      upgradesTotal += calculateUpgradePrice(u, passengerCounts)
      u.flightIds.forEach((id) => allUpgradeFlightIds.add(id))
    }
  }

  if (!allUpgradeFlightIds.size) return 0

  const seatDeduction = getSeatDeduction(flight.reservationAddons, allUpgradeFlightIds)
  return Math.max(0, upgradesTotal - seatDeduction)
}

export const getCharterUpgradeBreakdownView = createSelector(
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => state.checkout.charterUpgrade,
  (state: App.State) => state.orders.flightDetails,
  (postPurchaseMode, charterUpgrade, flightDetails): App.WithDataStatus<Array<App.Checkout.PriceBreakdownView>> => {
    if (postPurchaseMode !== 'charter-upgrade' || !charterUpgrade) {
      return { hasRequiredData: true, data: [] }
    }

    const details = flightDetails[charterUpgrade.flightItemId]
    if (!details?.flight) {
      return { hasRequiredData: false, data: [] }
    }

    const flight = details.flight
    const availableUpgrades = flight.upgradeOptions?.availableUpgrades ?? []
    const passengerCounts = getPassengerCounts(flight.travellers ?? [])

    let upgradesTotal = 0
    const allUpgradeFlightIds = new Set<string>()
    for (const leg of charterUpgrade.legs) {
      const upgrades = findMatchingUpgrades(availableUpgrades, leg.cabin, leg.direction)
      for (const u of upgrades) {
        upgradesTotal += calculateUpgradePrice(u, passengerCounts)
        u.flightIds.forEach((id) => allUpgradeFlightIds.add(id))
      }
    }

    if (!allUpgradeFlightIds.size) {
      return { hasRequiredData: true, data: [] }
    }

    const seatDeduction = getSeatDeduction(flight.reservationAddons, allUpgradeFlightIds)

    const intl = getCurrentIntl()
    const journeyLegs = flight.bookingJourney.legs

    // Use the first leg's direction to determine the route text
    const primaryDirection = charterUpgrade.legs[0]?.direction ?? 'both'
    const isMultiLegDifferentCabin = charterUpgrade.legs.length > 1
    let routeText = ''

    if (primaryDirection === 'both' || isMultiLegDifferentCabin) {
      const origin = journeyLegs[0]?.departureCity ?? ''
      const destination = journeyLegs[0]?.arrivalCity ?? ''

      routeText = origin && destination ? intl.formatMessage({
        id: 'IAnynthD61VcDoNvoV74',
        defaultMessage: 'Return {origin} to {destination}',
        description: 'Charter upgrade return route label shown in the checkout price breakdown',
      }, { origin, destination }) : ''
    } else {
      const legIndex = primaryDirection === 'return' ? 1 : 0
      const origin = journeyLegs[legIndex]?.departureCity ?? ''
      const destination = journeyLegs[legIndex]?.arrivalCity ?? ''

      routeText = origin && destination ? intl.formatMessage({
        id: 'IgmkWlONQofJ3XSrIT-X',
        defaultMessage: '{origin} to {destination}',
        description: 'Charter upgrade one-way route label shown in the checkout price breakdown',
      }, { origin, destination }) : ''
    }

    const passengerSummary = formatPassengerSummary(
      passengerCounts.numAdults,
      passengerCounts.numChildren,
      passengerCounts.numInfants,
    )

    const additionalInfo: Array<{ text: string }> = []
    if (routeText) additionalInfo.push({ text: routeText })
    if (passengerSummary) additionalInfo.push({ text: passengerSummary })

    const additionalElements: Array<App.Checkout.ItemBreakdownElement> = []
    if (seatDeduction > 0) {
      additionalElements.push({
        id: 'seat-deduction',
        type: 'price-line',
        left: {
          type: 'text',
          value: intl.formatMessage({
            id: '6bDRS5EyFpVOzS_mow6f',
            defaultMessage: 'Seat selection credit',
            description: 'Seat selection credit label shown in the charter upgrade checkout price breakdown',
          }),
        },
        right: { type: 'price', value: -seatDeduction },
      })
    }

    const items: Array<App.Checkout.CharterUpgradeBreakdownView> = [{
      itemType: 'charter-upgrade',
      title: '',
      additionalInfo,
      additionalElements,
    }]

    return {
      hasRequiredData: true,
      data: [{
        title: intl.formatMessage({
          id: 'XsIxMeeHcllZiON4Mudg',
          defaultMessage: 'Flight class upgrade',
          description: 'Charter upgrade section title in the checkout price breakdown',
        }),
        additionalInfoText: [],
        items,
        price: upgradesTotal,
        memberPrice: 0,
        ...(seatDeduction > 0 && { itemsAlwaysVisible: true, alwaysShowItemPrice: true }),
      }],
    }
  },
)
